import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  FilterOutlined,
  PlusOutlined,
  SettingOutlined,
  SortAscendingOutlined
} from '@ant-design/icons';
import {useUser} from '@dofleini/security';
import {Button, Checkbox, Dropdown, Input, Menu, Radio, Space, Tooltip} from 'antd';
import classNames from 'classnames';
// sort
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import React, {Fragment, memo, useCallback, useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';

import FilterArea from '@/components/TableFilters/components/FilterArea';
import FilterDropdown from '@/components/TableFilters/components/FilterDropdown';
//filter
import {TableFiltersProvider} from '@/components/TableFilters/contexts/TableFiltersContext';
import {useFilter} from '@/contexts/FilterContext';
import {useResponsive} from '@/contexts/ResponsiveContext';
import {TABLE_SIZE, useTableGrid} from '@/contexts/tableGridContact';
//hooks
import {usePermissions} from '@/hooks/usePermissions';

const {SubMenu} = Menu;

const TableMenu = () => {
  const {handleChange, size, visibleColumns, handleDeletedCols, toggleAllHidden, columns} = useTableGrid();
  const {t} = useTranslation('table');
  const someColumnHidden = visibleColumns.length > 0 && visibleColumns.length !== columns.length;
  const allVisible = visibleColumns.length === columns.length;

  const menu = (
    <Menu onClick={handleChange}>
      <SubMenu title={t('tableSize')}>
        <Menu.Item key={TABLE_SIZE.big}>
          <div className={'w-40'}>
            <Checkbox checked={size === TABLE_SIZE.big}> {t('big')}</Checkbox>
          </div>
        </Menu.Item>
        <Menu.Item key={TABLE_SIZE.middle}>
          <div className={'w-40'}>
            <Checkbox checked={size === TABLE_SIZE.middle}> {t('medium')}</Checkbox>
          </div>
        </Menu.Item>
        <Menu.Item key={TABLE_SIZE.small}>
          <div className={'w-40'}>
            <Checkbox checked={size === TABLE_SIZE.small}> {t('small')}</Checkbox>
          </div>
        </Menu.Item>
      </SubMenu>
      <SubMenu title={t('showHideColumns')}>
        <div className={'pt-1'}>
          <div className={'py-1 px-3 border-0 border-b border-solid border-gray-300 mb-3'}>
            <Space>
              <Checkbox
                checked={allVisible}
                indeterminate={someColumnHidden}
                onChange={() => toggleAllHidden(someColumnHidden ? true : !allVisible)}>{t('showColumns')}</Checkbox>
              <Button type={'link'} onClick={() => toggleAllHidden(true)} disabled={allVisible}>
                {t('reset')}
              </Button>
            </Space>
          </div>
          {
            columns.map(c => <div key={`col-${c.dataIndex}`} className={'py-1 px-3 hover:bg-item-hover-bg'}>
              <Checkbox checked={c.visible} onChange={() => handleDeletedCols(c.dataIndex)}>
                {c.title}
              </Checkbox>
            </div>)
          }
        </div>
      </SubMenu>
    </Menu>
  );

  return <div className={'hidden lg:block'}>
    <Dropdown overlay={menu} trigger={['click']}>
      <Tooltip title={t('tableSettings')}>
        <Button icon={<SettingOutlined/>} type={'text'}/>
      </Tooltip>
    </Dropdown>
  </div>;
};

const SortDropdownMenu = ({tableHook, pageConfig}) => {
  const {
    handleChange,
    columns,
    handleChangeSort,
    sortKey,
    sortDirection,
    sort
  } = useTableGrid();
  const {isMobile} = useResponsive();
  const {setSort} = tableHook();
  const {t} = useTranslation('table');

  useEffect(() => {
    setSort && setSort(pageConfig?.sort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSort && setSort(sort);
  }, [pageConfig?.sort, setSort, sort]);

  const onSortChange = useCallback(({target}) => {
    const [key, direction] = target?.value?.split('-');
    switch (direction) {
      case 'asc':
        return handleChangeSort({sortKey: key, sortDirection: 1});
      case 'desc':
        return handleChangeSort({sortKey: key, sortDirection: -1});
      default:
        return;
    }
  }, [handleChangeSort]);

  const currentSort = useMemo(() => {
    if (!sortKey) return null;
    return `${sortKey}-${sortDirection === 1 ? 'asc' : 'desc'}`;
  }, [sortDirection, sortKey]);

  const sortOptions = useMemo(() => {
    return columns.filter(e => e?.sorteable);
  }, [columns]);

  const menu = (
    <Menu onClick={handleChange}>
      <Radio.Group value={currentSort} onChange={onSortChange}>
        {
          sortOptions.map((c, index) => {
            const queryKey = Array.isArray(c.dataIndex) ? c.dataIndex?.join('.') : c.dataIndex;
            
            return <div key={`col-${c.dataIndex}-asc` || index}>
              <div key={`col-${c.dataIndex}-asc`} className={'py-1 px-3 hover:bg-item-hover-bg'}>
                {`${c.title} ${t('sort.asc')}`}
                <Radio value={`${queryKey}-asc`}>
                  <span className="mr-2">{c.title}</span>
                  <ArrowUpOutlined/>
                </Radio>
              </div>
              <div key={`col-${c.dataIndex}-desc`} className={'py-1 px-3 hover:bg-item-hover-bg'}>
                <Radio value={`${queryKey}-desc`}>
                  <span className="mr-2">{c.title}</span>
                  <ArrowDownOutlined/>
                </Radio>
              </div>
            </div>;
          })
        }
      </Radio.Group>
    </Menu>
  );

  if (isEmpty(sortOptions)) return Fragment;

  return <div className={'hidden lg:block'}>
    <Dropdown overlay={menu} trigger={['click']}>
      <Button icon={<SortAscendingOutlined/>}>{!isMobile && t('sort.title')}</Button>
    </Dropdown>
  </div>;
};

const AddButton = ({onAdd, addLabel, keepLabel}) => {
  return (<Button type="primary" onClick={onAdd} icon={<PlusOutlined/>}>
    <div
      className={classNames({'ml-2 hidden md:inline': !keepLabel}, {'ml-2 inline': keepLabel})}>{addLabel}</div>
  </Button>
  );
};

const AddButtonConfig = ({useCreatePath, route, onAdd, addLabel, ...props}) => {
  if (useCreatePath && route)
    return <Link to={`${route}/create`}>
      <AddButton addLabel={addLabel} {...props}/>
    </Link>;
  if (onAdd)
    return <AddButton addLabel={addLabel} onAdd={onAdd} {...props}/>;
  return <></>;
};

const TableHeader = ({
  title,
  canSearch,
  route,
  useCreatePath,
  addLabel,
  onAdd,
  searchPlaceholder,
  extras,
  hasTable,
  canAdd,
  translation,
  filters,
  extraFilters,
  subTitle,
  searchSize,
  entityName,
  tabs,
  ...props
}) => {
  const {user} = useUser();
  const {setSearch} = useFilter();
  const {isMobile} = useResponsive();
  const {handleChangePage} = useTableGrid();
  const {t} = useTranslation('table');
  const {canWrite} = usePermissions(entityName);

  const onSearchRequest = useCallback((e) => {
    setSearch(e);
    // reset page
    handleChangePage?.(1);
  }, [handleChangePage, setSearch]);

  const enabledFilters = useMemo(() => filters?.filter(f => {
    return f?.enableFnc ? f?.enableFnc(user, entityName) : true;
  }), [entityName, filters, user]);

  return (
    <TableFiltersProvider filters={enabledFilters}>
      <div className={classNames('min-h-12', {'mb-5': !tabs})}>
        <div className={'flex flex-col md:flex-row md:items-center'}>
          <div>
            <h1 className={'uppercase mb-0 whitespace-nowrap text-xl'}>{title}</h1>
            {subTitle && <div className={'mb-0 whitespace-nowrap md:max-w-3/4'}>{subTitle}</div>}
          </div>
          <FilterArea translation={translation}/>
          <Space size={isMobile ? 'small' : 'middle'}>
            <FilterDropdown translation={translation}>
              <Button icon={<FilterOutlined/>}>{!isMobile && t('filters')}</Button>
            </FilterDropdown>
            {hasTable && <SortDropdownMenu {...props}/>}
            {canSearch &&
              <Input.Search
                size={searchSize}
                title={searchPlaceholder || t('searchPlaceholder')}
                className="min-w-48"
                allowClear
                placeholder={searchPlaceholder || t('searchPlaceholder')}
                onSearch={onSearchRequest}
                enterButton/>
            }
            {
              canAdd && canWrite &&
              <AddButtonConfig onAdd={onAdd} addLabel={addLabel} route={route}
                useCreatePath={useCreatePath} {...props}/>
            }
            {extras}
            {hasTable && <TableMenu/>}
          </Space>
        </div>
        <div className={classNames('flex items-center justify-end', {'mt-2': !!extraFilters})}>{extraFilters}</div>
      </div>
    </TableFiltersProvider>
  );
};

export default memo(TableHeader);

TableHeader.propTypes = {
  addLabel: PropTypes.string,
  searchSize: PropTypes.string,
  addText: PropTypes.string,
  canSearch: PropTypes.bool,
  keepLabel: PropTypes.bool,
  extras: PropTypes.node,
  filters: PropTypes.any,
  hasTable: PropTypes.bool,
  onAdd: PropTypes.func,
  route: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  title: PropTypes.string,
  subTitle: PropTypes.any,
  tabs: PropTypes.any,
  translation: PropTypes.string,
  translationPrefix: PropTypes.string,
  canAdd: PropTypes.bool,
  useCreatePath: PropTypes.bool,
  entityName: PropTypes.string,
  extraFilters: PropTypes.any
};

TableHeader.defaultProps = {
  addText: 'Añadir'
};

AddButtonConfig.propTypes = {
  addLabel: PropTypes.string,
  onAdd: PropTypes.func,
  route: PropTypes.string,
  useCreatePath: PropTypes.bool
};

SortDropdownMenu.propTypes = {
  tableHook: PropTypes.any,
  pageConfig: PropTypes.object,
};

SortDropdownMenu.defaultProps = {
  tableHook: () => {
  }
};

AddButton.propTypes = {
  addLabel: PropTypes.string,
  keepLabel: PropTypes.bool,
  onAdd: PropTypes.func
};
